import clsx from 'clsx';
import {
  forwardRef,
  useId,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react';
import styles from './Field.module.css';

interface FrameProps {
  label: ReactNode;
  error?: string;
  hint?: ReactNode;
  /** Fixed prefix inside the box, e.g. «+212». */
  prefix?: ReactNode;
  /** Trailing slot, e.g. the show-password button. */
  suffix?: ReactNode;
  className?: string;
}

function Frame({
  id,
  label,
  error,
  hint,
  prefix,
  suffix,
  className,
  children,
}: FrameProps & { id: string; children: ReactNode }) {
  return (
    <div className={clsx(styles.wrap, className)}>
      <div className={clsx(styles.fld, error && styles.invalid)}>
        <label htmlFor={id}>{label}</label>
        {prefix || suffix ? (
          <div className={styles.inline}>
            {prefix && <b className={styles.prefix}>{prefix}</b>}
            {children}
            {suffix}
          </div>
        ) : (
          children
        )}
      </div>
      {error ? (
        <p id={`${id}-err`} className={styles.error} role="alert">
          {error}
        </p>
      ) : hint ? (
        <p id={`${id}-hint`} className={styles.hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}

function describedBy(id: string, error?: string, hint?: ReactNode) {
  return error ? `${id}-err` : hint ? `${id}-hint` : undefined;
}

export type TextFieldProps = FrameProps & Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'>;

/** Text input with the label inside the box (`.fld`). Works with react-hook-form's `register`. */
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField(
  { label, error, hint, prefix, suffix, className, id, ...input },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Frame id={fid} label={label} error={error} hint={hint} prefix={prefix} suffix={suffix} className={className}>
      <input
        ref={ref}
        id={fid}
        aria-invalid={!!error || undefined}
        aria-describedby={describedBy(fid, error, hint)}
        {...input}
      />
    </Frame>
  );
});

export type SelectFieldProps = FrameProps & SelectHTMLAttributes<HTMLSelectElement>;

export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(function SelectField(
  { label, error, hint, className, id, children, ...select },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Frame id={fid} label={label} error={error} hint={hint} className={className}>
      <select
        ref={ref}
        id={fid}
        aria-invalid={!!error || undefined}
        aria-describedby={describedBy(fid, error, hint)}
        {...select}
      >
        {children}
      </select>
    </Frame>
  );
});

export type TextAreaFieldProps = FrameProps & TextareaHTMLAttributes<HTMLTextAreaElement>;

export const TextAreaField = forwardRef<HTMLTextAreaElement, TextAreaFieldProps>(function TextAreaField(
  { label, error, hint, className, id, ...area },
  ref,
) {
  const auto = useId();
  const fid = id ?? auto;
  return (
    <Frame id={fid} label={label} error={error} hint={hint} className={className}>
      <textarea
        ref={ref}
        id={fid}
        aria-invalid={!!error || undefined}
        aria-describedby={describedBy(fid, error, hint)}
        {...area}
      />
    </Frame>
  );
});
